import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Archive, ArrowLeft, LogOut, Pencil, Plus, UserPlus, X } from 'lucide-react';
import { type FormEvent, useMemo, useState } from 'react';
import { useMe } from '~/api/auth';
import { inlineError } from '~/api/client';
import { peopleQueryOptions } from '~/api/people';
import {
  PROJECT_KIND_LABEL,
  PROJECT_STATUS_LABEL,
  projectBoardQueryOptions,
  projectQueryOptions,
  type TaskStage,
  projectSummaryQueryOptions,
  projectUpdatesQueryOptions,
  useAddProjectMember,
  useAddProjectUpdate,
  useArchiveProject,
  useRemoveProjectMember,
} from '~/api/projects';
import { useServerClock } from '~/api/serverClock';
import { listsQueryOptions, occurrencesQueryOptions, tasksQueryOptions } from '~/api/work';
import {
  HEALTH_LABEL,
  HEALTH_TONE,
  PROJECT_COLOR_LABEL,
  type ProjectColor,
  STATUS_TONE,
} from '~/features/projects/tones';
import { ProjectDialog } from '~/features/projects/ProjectDialog';
import { LeaveProjectDialog, ProjectTimeline } from '~/features/projects/ProjectTimeline';
import { ProjectKey } from '~/features/projects/ProjectColor';
import { chatListQueryOptions } from '~/api/teamChat';
import { Conversation } from '~/features/team-chat/Conversation';
import { Board } from '~/features/work/Board';
import { OccurrenceSheet } from '~/features/work/OccurrenceSheet';
import { TaskList } from '~/features/work/TaskList';
import { TaskSheet } from '~/features/work/TaskSheet';
import { addDays, calendarDate, describeDue, formatMinutes, relativeTime } from '~/lib/format';
import { notify } from '~/lib/notify';
import { Avatar } from '~/ui/Avatar';
import { Bar } from '~/ui/Bar';
import { Button } from '~/ui/Button';
import { Card } from '~/ui/Card';
import { useConfirm } from '~/ui/Confirm';
import { Donut } from '~/ui/Donut';
import { ErrorNotice } from '~/ui/ErrorNotice';
import { Input } from '~/ui/Input';
import { Notice } from '~/ui/Notice';
import { PageHeader } from '~/ui/PageHeader';
import { Pill } from '~/ui/Pill';
import { Select } from '~/ui/Select';
import { Skeleton } from '~/ui/Skeleton';
import { Tabs } from '~/ui/Tabs';

type View = 'board' | 'overview' | 'timeline' | 'chat';

interface Search {
  view?: View;
  task?: string;
  run?: string;
}

export const Route = createFileRoute('/_app/projects/$id')({
  validateSearch: (s: Record<string, unknown>): Search => ({
    view:
      s['view'] === 'overview' || s['view'] === 'timeline' || s['view'] === 'chat'
        ? s['view']
        : undefined,
    task: typeof s['task'] === 'string' && s['task'] ? s['task'] : undefined,
    run: typeof s['run'] === 'string' && s['run'] ? s['run'] : undefined,
  }),
  component: ProjectPage,
});

function ProjectPage() {
  const { id } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: '/projects/$id' });
  const me = useMe();
  const now = useServerClock(60_000);
  const today = calendarDate(now, me.person.tz ?? me.workspace.tz);
  const project = useQuery(projectQueryOptions(id));
  const summary = useQuery(projectSummaryQueryOptions(id));
  // The project's chat, from the cached list; absent for a personal project or someone not on it.
  const chats = useQuery(chatListQueryOptions);
  const channel = chats.data?.find((c) => c.projectId === id);
  const lists = useQuery(listsQueryOptions);
  const [editing, setEditing] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [newStage, setNewStage] = useState<TaskStage | undefined>(undefined);
  const view: View = search.view ?? 'board';
  const archive = useArchiveProject();
  const confirm = useConfirm();
  const go = (patch: Partial<Search>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });

  if (project.isPending) return <Skeleton lines={10} />;
  if (project.isError)
    return <ErrorNotice error={project.error} onRetry={() => project.refetch()} />;
  const p = project.data;
  const s = summary.data;
  const isLead =
    me.person.role === 'owner' ||
    p.leadPersonId === me.person.id ||
    p.members.some((m) => m.personId === me.person.id && m.role === 'lead');
  // A project that keeps tasks to their people shows a member theirs and the pool only.
  const seesOwnOnly = p.kind === 'team' && !p.tasksShared && !isLead;
  // A member may leave a team project; the main lead hands the lead on first.
  const canLeave =
    p.kind === 'team' &&
    p.status !== 'archived' &&
    p.leadPersonId !== me.person.id &&
    p.members.some((m) => m.personId === me.person.id);

  return (
    <div className="mx-auto max-w-[1240px]">
      <Link
        to="/projects"
        className="mb-3 inline-flex items-center gap-1 text-sm text-ink-2 hover:text-ink"
      >
        <ArrowLeft size={14} aria-hidden /> All projects
      </Link>
      <PageHeader
        eyebrow="Project"
        title={p.name}
        subtitle={p.goal ?? undefined}
        actions={
          isLead && p.status !== 'archived' ? (
            <>
              {canLeave && (
                <Button
                  variant="quiet"
                  icon={<LogOut size={14} aria-hidden />}
                  onClick={() => setLeaving(true)}
                >
                  Leave
                </Button>
              )}
              <Button
                variant="secondary"
                icon={<Pencil size={14} aria-hidden />}
                onClick={() => setEditing(true)}
              >
                Change
              </Button>
              <Button
                variant="quiet"
                icon={<Archive size={14} aria-hidden />}
                loading={archive.isPending}
                onClick={async () => {
                  if (
                    !(await confirm({
                      title: `Archive ${p.name}?`,
                      body: 'Its tasks and history stay.',
                      confirmLabel: 'Archive',
                      tone: 'danger',
                    }))
                  )
                    return;
                  try {
                    await archive.mutateAsync(p.id);
                    notify.success('Archived.');
                  } catch (e) {
                    notify.error('Could not archive.', e);
                  }
                }}
              >
                Archive
              </Button>
            </>
          ) : canLeave ? (
            <Button
              variant="quiet"
              icon={<LogOut size={14} aria-hidden />}
              onClick={() => setLeaving(true)}
            >
              Leave project
            </Button>
          ) : undefined
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <ProjectKey project={p} title={`${PROJECT_COLOR_LABEL[p.color]} project`} />
        <Pill>{PROJECT_KIND_LABEL[p.kind]}</Pill>
        <Pill tone={STATUS_TONE[p.status]}>{PROJECT_STATUS_LABEL[p.status]}</Pill>
        {s && <Pill tone={HEALTH_TONE[s.health]}>{HEALTH_LABEL[s.health]}</Pill>}
        {p.targetDate && <Pill>Target {describeDue(p.targetDate, today)}</Pill>}
        {seesOwnOnly && <Pill>You see your tasks and unassigned ones</Pill>}
      </div>

      <div className="mb-4">
        <Tabs
          label="Project view"
          value={view}
          onChange={(v) => go({ view: v === 'board' ? undefined : v })}
          options={[
            { value: 'board', label: 'Board' },
            { value: 'overview', label: 'Overview' },
            { value: 'timeline', label: 'Timeline' },
            ...(channel
              ? [
                  {
                    value: 'chat' as const,
                    label: 'Chat',
                    count: channel.unread > 0 ? channel.unread : undefined,
                  },
                ]
              : []),
          ]}
        />
      </div>

      {view === 'chat' && channel ? (
        <div className="flex h-[min(720px,calc(100dvh-220px))] min-h-[420px] flex-col overflow-hidden rounded-panel border border-line bg-paper">
          <Conversation key={channel.id} id={channel.id} embedded />
        </div>
      ) : view === 'timeline' ? (
        <ProjectTimeline projectId={p.id} onOpenTask={(tid) => go({ task: tid })} />
      ) : view === 'board' ? (
        <ProjectBoardView
          project={p}
          today={today}
          canAdd={p.status !== 'archived'}
          onOpenTask={(tid) => go({ task: tid })}
          onOpenRun={(rid) => go({ run: rid })}
          onNew={(stage) => {
            setNewStage(stage);
            go({ task: 'new' });
          }}
        />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <Card
              title="Progress"
              eyebrow={
                s?.progressSource === 'updates' ? 'From the latest update' : 'From finished tasks'
              }
            >
              {!s ? (
                <Skeleton lines={5} />
              ) : (
                <div className="flex flex-col items-center gap-3">
                  <Donut
                    percent={s.progressPercent}
                    label={s.health === 'at_risk' ? 'at risk' : 'complete'}
                  />
                  <dl className="grid w-full grid-cols-3 gap-2 text-center text-xs">
                    <div>
                      <dt className="text-ink-3">Done</dt>
                      <dd className="font-display text-lg font-semibold tabular">{s.runs.done}</dd>
                    </div>
                    <div>
                      <dt className="text-ink-3">Open</dt>
                      <dd className="font-display text-lg font-semibold tabular">{s.runs.open}</dd>
                    </div>
                    <div>
                      <dt className="text-ink-3">Overdue</dt>
                      <dd
                        className={`font-display text-lg font-semibold tabular ${s.runs.overdue > 0 ? 'text-late' : ''}`}
                      >
                        {s.runs.overdue}
                      </dd>
                    </div>
                  </dl>
                </div>
              )}
            </Card>

            <Card title="Hours" eyebrow="Spent against the budget" delay={40}>
              {!s ? (
                <Skeleton lines={3} />
              ) : (
                <div className="flex flex-col gap-3">
                  <p className="font-display text-[34px] leading-none font-semibold tabular">
                    {formatMinutes(s.hours.spentMinutes)}
                  </p>
                  {s.hours.budgetMinutes !== null ? (
                    <>
                      <Bar
                        value={s.hours.spentMinutes}
                        max={s.hours.budgetMinutes}
                        tone={s.hours.spentMinutes > s.hours.budgetMinutes ? 'late' : 'accent'}
                      />
                      <p className="text-xs text-ink-3">
                        of {formatMinutes(s.hours.budgetMinutes)} budgeted
                        {s.hours.used !== null ? ` · ${Math.round(s.hours.used * 100)}% used` : ''}
                      </p>
                    </>
                  ) : (
                    <p className="text-xs text-ink-3">No hour budget set.</p>
                  )}
                  <p className="text-xs text-ink-3">
                    {s.tasks.total} tasks: {s.tasks.oneTime} one-time ({s.tasks.oneTimeDone} done),{' '}
                    {s.tasks.recurring} repeating
                    {s.tasks.pool > 0 ? `, ${s.tasks.pool} in the pool` : ''}.
                  </p>
                </div>
              )}
            </Card>

            <MembersCard
              projectId={p.id}
              members={p.members}
              summary={s?.members ?? []}
              isLead={isLead && p.status !== 'archived'}
              leadId={p.leadPersonId}
              delay={80}
            />
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
            <Card
              title="Tasks"
              eyebrow="In this project"
              className="lg:col-span-2"
              delay={120}
              actions={
                p.status !== 'archived' ? (
                  <Button
                    size="sm"
                    icon={<Plus size={14} aria-hidden />}
                    onClick={() => {
                      setNewStage(undefined);
                      go({ task: 'new' });
                    }}
                  >
                    New task
                  </Button>
                ) : undefined
              }
            >
              <ProjectTasks projectId={p.id} today={today} onOpen={(tid) => go({ task: tid })} />
            </Card>
            <UpdatesCard projectId={p.id} delay={160} />
          </div>
        </>
      )}

      {isLead && <ProjectDialog open={editing} onClose={() => setEditing(false)} project={p} />}
      {canLeave && (
        <LeaveProjectDialog
          projectId={p.id}
          projectName={p.name}
          open={leaving}
          onClose={() => setLeaving(false)}
          onLeft={() => void navigate({ to: '/projects' })}
        />
      )}
      <TaskSheet
        id={search.task ?? null}
        newProjectId={p.id}
        newStage={newStage}
        lists={lists.data ?? []}
        today={today}
        onClose={() => go({ task: undefined })}
        onOpenRun={(rid) => go({ task: undefined, run: rid })}
        onOpenTask={(tid) => go({ task: tid })}
      />
      <OccurrenceSheet
        id={search.run ?? null}
        today={today}
        onClose={() => go({ run: undefined })}
        onOpenTask={(tid) => go({ run: undefined, task: tid })}
      />
    </div>
  );
}

/**
 * The project's board is the tasks page's board narrowed to this project: the
 * same runs, columns, cards and moves, so a card moved here is moved there.
 * It keeps the order people give the cards; ideas wait in the backlog, and
 * repeating duties sit beside the board.
 */
function ProjectBoardView({
  project,
  today,
  canAdd,
  onOpenTask,
  onOpenRun,
  onNew,
}: {
  project: { id: string; key: string; name: string; color: ProjectColor };
  today: string;
  canAdd: boolean;
  onOpenTask: (id: string) => void;
  onOpenRun: (id: string) => void;
  onNew: (stage: TaskStage) => void;
}) {
  const board = useQuery(projectBoardQueryOptions(project.id));
  const runs = useQuery(
    occurrencesQueryOptions({
      projectId: project.id,
      from: addDays(today, -90),
      to: addDays(today, 30),
      includeUndated: true,
      includeOpen: true,
    }),
  );
  const projectById = useMemo(
    () => new Map([[project.id, { key: project.key, name: project.name, color: project.color }]]),
    [project.id, project.key, project.name, project.color],
  );
  if (board.isPending || runs.isPending) return <Skeleton lines={8} />;
  if (board.isError) return <ErrorNotice error={board.error} onRetry={() => board.refetch()} />;
  if (runs.isError) return <ErrorNotice error={runs.error} onRetry={() => runs.refetch()} />;
  const b = board.data;
  return (
    <div className="flex flex-col gap-4">
      <Board
        occurrences={runs.data.filter((o) => !o.recurring)}
        today={today}
        serverTime={new Date(runs.dataUpdatedAt).toISOString()}
        onOpen={onOpenRun}
        projectById={projectById}
        onNew={canAdd ? onNew : undefined}
        ranked
        ideas={b.cards
          .filter((c) => c.status === 'idea')
          .map((c) => ({ taskId: c.taskId, ref: c.ref, title: c.title }))}
        onOpenTask={onOpenTask}
      />
      {b.repeating.length > 0 && (
        <Card
          title="Repeating duties"
          eyebrow="They come back on their schedule, so they sit beside the board"
        >
          <ul className="divide-y divide-line">
            {b.repeating.map((r) => (
              <li key={r.taskId}>
                <button
                  type="button"
                  onClick={() => onOpenTask(r.taskId)}
                  className="flex w-full items-center gap-3 py-2 text-left text-sm hover:text-accent"
                >
                  {r.ref && <span className="font-mono text-[11px] text-ink-3">{r.ref}</span>}
                  <span className="flex-1 truncate">{r.title}</span>
                  <span className="text-xs text-ink-3">{r.ownerName ?? 'In the pool'}</span>
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

function ProjectTasks({
  projectId,
  today,
  onOpen,
}: {
  projectId: string;
  today: string;
  onOpen: (id: string) => void;
}) {
  const tasks = useQuery(tasksQueryOptions({ projectId }));
  const lists = useQuery(listsQueryOptions);
  if (tasks.isPending || lists.isPending) return <Skeleton lines={4} />;
  if (tasks.isError) return <ErrorNotice error={tasks.error} onRetry={() => tasks.refetch()} />;
  if (lists.isError) return <ErrorNotice error={lists.error} onRetry={() => lists.refetch()} />;
  if (tasks.data.length === 0)
    return <p className="text-sm text-ink-3">No tasks in this project yet.</p>;
  // A project's tasks come in one go; each section shows them all.
  const sections = lists.data.map((list) => {
    const rows = tasks.data.filter((t) => t.listId === list.id);
    return { list, tasks: rows, total: rows.length };
  });
  return <TaskList sections={sections} today={today} onOpen={onOpen} />;
}

const ROLE_NAME = { lead: 'Co-lead', member: 'Member' } as const;

/**
 * The people on the project. The project lead runs it; co-leads can do the
 * same (change it, add people, set roles); members work on it. The lead
 * changes from the Change button.
 */
function MembersCard({
  projectId,
  members,
  summary,
  isLead,
  leadId,
  delay,
}: {
  projectId: string;
  members: { personId: string; name: string; role: 'lead' | 'member' }[];
  summary: { personId: string; spentMinutes: number; doneCount: number; openCount: number }[];
  isLead: boolean;
  leadId: string | null;
  delay: number;
}) {
  const people = useQuery({ ...peopleQueryOptions({ status: 'active' }), enabled: isLead });
  const add = useAddProjectMember(projectId);
  const remove = useRemoveProjectMember(projectId);
  const [adding, setAdding] = useState('');
  const [addRole, setAddRole] = useState<'lead' | 'member'>('member');
  const candidates = (people.data ?? []).filter((p) => !members.some((m) => m.personId === p.id));
  const fail = (title: string) => (e: unknown) => notify.error(title, e);
  const ordered = [...members].sort(
    (a, b) =>
      Number(b.personId === leadId) - Number(a.personId === leadId) ||
      Number(b.role === 'lead') - Number(a.role === 'lead') ||
      a.name.localeCompare(b.name),
  );
  return (
    <Card title="People" eyebrow={`${members.length} on it`} delay={delay}>
      <ul className="divide-y divide-line">
        {ordered.map((m) => {
          const st = summary.find((x) => x.personId === m.personId);
          const isMain = m.personId === leadId;
          return (
            <li key={m.personId} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
              <Avatar name={m.name} size="sm" personId={m.personId} />
              <div className="min-w-36 flex-1">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="truncate font-medium">{m.name}</span>
                  {isMain && <Pill tone="accent">Project lead</Pill>}
                  {!isMain && m.role === 'lead' && !isLead && <Pill tone="accent">Co-lead</Pill>}
                </div>
                {st && (
                  <p className="text-xs text-ink-3">
                    {formatMinutes(st.spentMinutes)} · {st.doneCount} done · {st.openCount} open
                  </p>
                )}
              </div>
              {isLead && !isMain && (
                <div className="ml-auto flex items-center gap-2">
                  <select
                    aria-label={`Role of ${m.name}`}
                    value={m.role}
                    disabled={add.isPending}
                    onChange={(e) =>
                      add
                        .mutateAsync({
                          personId: m.personId,
                          role: e.target.value as 'lead' | 'member',
                        })
                        .then(() =>
                          notify.success(
                            `${m.name} is now a ${ROLE_NAME[e.target.value as 'lead' | 'member'].toLowerCase()}.`,
                          ),
                        )
                        .catch(fail('Could not change the role.'))
                    }
                    className="h-8 rounded-control border border-line bg-paper px-2 text-xs"
                  >
                    <option value="member">Member</option>
                    <option value="lead">Co-lead</option>
                  </select>
                  <button
                    type="button"
                    aria-label={`Remove ${m.name}`}
                    onClick={() => remove.mutateAsync(m.personId).catch(fail('Could not remove.'))}
                    className="grid size-7 place-items-center rounded-control text-ink-3 hover:bg-late-soft hover:text-late"
                  >
                    <X size={14} aria-hidden />
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {isLead && candidates.length > 0 && (
        <form
          className="mt-3 flex flex-wrap items-end gap-2"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!adding) return;
            try {
              await add.mutateAsync({ personId: adding, role: addRole });
              setAdding('');
              setAddRole('member');
            } catch (err) {
              fail('Could not add.')(err);
            }
          }}
        >
          <div className="min-w-[140px] flex-1">
            <Select label="Add someone" value={adding} onChange={(e) => setAdding(e.target.value)}>
              <option value="">Choose</option>
              {candidates.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </Select>
          </div>
          <div className="w-28">
            <Select
              label="As"
              value={addRole}
              onChange={(e) => setAddRole(e.target.value as 'lead' | 'member')}
            >
              <option value="member">Member</option>
              <option value="lead">Co-lead</option>
            </Select>
          </div>
          <Button
            type="submit"
            variant="secondary"
            icon={<UserPlus size={14} aria-hidden />}
            loading={add.isPending}
            disabled={!adding}
          >
            Add
          </Button>
        </form>
      )}
      {isLead && (
        <p className="mt-3 text-xs text-ink-3">
          Co-leads can change the project and its people. To hand over the project lead, use Change
          at the top.
        </p>
      )}
    </Card>
  );
}

function UpdatesCard({ projectId, delay }: { projectId: string; delay: number }) {
  const updates = useQuery(projectUpdatesQueryOptions(projectId));
  const add = useAddProjectUpdate(projectId);
  const [text, setText] = useState('');
  const [percent, setPercent] = useState('');
  const addMessage = inlineError(add.error);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await add.mutateAsync({
        text: text.trim(),
        percent: percent === '' ? null : Number(percent),
      });
      setText('');
      setPercent('');
    } catch {
      // inline
    }
  };
  return (
    <Card title="Updates" eyebrow="Progress notes" delay={delay}>
      <form onSubmit={submit} className="mb-4 flex flex-col gap-2" noValidate>
        <textarea
          className="min-h-[64px] w-full rounded-control border border-line bg-paper px-3 py-2 text-sm focus:border-accent"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Where it stands, what is next."
        />
        <div className="flex items-end gap-2">
          <div className="w-24">
            <Input
              label="%"
              type="number"
              min={0}
              max={100}
              mono
              optional
              value={percent}
              onChange={(e) => setPercent(e.target.value)}
            />
          </div>
          <Button type="submit" size="sm" loading={add.isPending} disabled={text.trim().length < 2}>
            Post
          </Button>
        </div>
        {addMessage && <Notice tone="late">{addMessage}</Notice>}
      </form>
      {updates.isPending ? (
        <Skeleton lines={3} />
      ) : updates.isError ? (
        <ErrorNotice error={updates.error} onRetry={() => updates.refetch()} />
      ) : updates.data.length === 0 ? (
        <p className="text-sm text-ink-3">No updates yet.</p>
      ) : (
        <ol className="relative flex flex-col gap-4 border-l border-line pl-4">
          {updates.data.map((u) => (
            <li key={u.id} className="relative text-sm">
              <span
                aria-hidden
                className="absolute top-1.5 -left-[21px] size-2.5 rounded-full bg-accent"
              />
              <div className="mb-0.5 flex items-center gap-2 text-xs text-ink-3">
                <span className="font-medium text-ink-2">{u.personName ?? 'Someone'}</span>
                <span>{relativeTime(u.createdAt)}</span>
                {u.percent !== null && <Pill tone="accent">{u.percent}%</Pill>}
              </div>
              <p className="whitespace-pre-wrap">{u.text}</p>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}
